import {Component, OnInit} from '@angular/core';
import {CartService, CartWithLineItems, LineItem} from '../../shopping-cart/cart.service';
import {Cart, CartStatus} from '../../shopping-cart/Cart';
import {catchError, concatMap, first, map, mergeMap, tap} from 'rxjs/operators';
import {UserService} from '@uofu-uss-aux/software-catalog-ng-core';
import {
  emptyPage,
  NotificationService,
  Page,
  PageComponent
} from '@uofu-uss/angular-util';
import {orderBy} from 'lodash-es';
import {BehaviorSubject, from, Observable, of} from 'rxjs';
import {OrderSearchComponent, OrderSearchCriteria} from '../order-search/order-search.component';
import {SearchHistoryService} from '../search-history/search-history.service';
import { HttpErrorResponse } from '@angular/common/http';
import {PageService} from '../page/page.service';
import {NgForOf, NgIf} from '@angular/common';
import {PastOrderComponent} from '../past-order/past-order.component';

@Component({
  selector: 'app-account-history',
  templateUrl: './account-history.component.html',
  styleUrls: ['./account-history.component.scss'],
  standalone: true,
  imports: [
    OrderSearchComponent,
    NgIf,
    PastOrderComponent,
    NgForOf,
    PageComponent
  ]
})
export class AccountHistoryComponent implements OnInit {

  static readonly allowedCartStatuses = [CartStatus.Purchased, CartStatus.POPending, CartStatus.Cancelled];

  private cartsAndItemsForDisplay = new BehaviorSubject<ReadonlyArray<CartWithLineItems>>([]);
  private pages: ReadonlyArray<Page<CartWithLineItems>> = [];

  #isSearching = false;

  currentPageIndex = 0;
  pulledSomeOrders = false;
  cartsAndItems: ReadonlyArray<CartWithLineItems> = [];
  ordersToView = false;

  constructor(private readonly cartService: CartService,
              private readonly userService: UserService,
              private readonly pageService: PageService,
              private readonly searchHistoryService: SearchHistoryService,
              private readonly notificationService: NotificationService) {
  }

  ngOnInit(): void {
    this.cartsAndItemsForDisplay.subscribe(() => this.pages = this.pageService.mapDataToPages(this.cartsWithItems()));
    this.loadCarts();
  }

  private loadCarts(): void {
    const observer = {
      next: this.loadCartWithLineItemsSuccess,
      error: this.loadCartWithLineItemsError,
    };

    this.cartService.fetchCarts(AccountHistoryComponent.allowedCartStatuses, this.userService.value?.unid)
      .pipe(
        first(),
        tap(carts => this.addLoadedCarts(carts)),
        concatMap(carts => this.generateCartWithLineItemsObservable(carts)))
      .subscribe(observer);
  }

  readonly loadCartWithLineItemsSuccess = (loaded: CartWithLineItems) => {
    const index = this.cartsAndItems.findIndex(cwi => cwi.cart.id === loaded.cart.id);
    const copy = this.cartsAndItems.slice();
    copy[index] = loaded;
    this.cartsAndItems = copy;
    this.cartsAndItemsForDisplay.next(this.cartsAndItems);
  }

  readonly loadCartWithLineItemsError = (error: HttpErrorResponse) => {
    this.notificationService.addError({
      title: 'Unable to fetch history',
      body: error.message || 'problem fetching carts'
    });
    this.ordersToView = false;
  }

  private generateCartWithLineItemsObservable(carts: ReadonlyArray<Cart>): Observable<CartWithLineItems> {
    return from(carts).pipe(mergeMap(cart => this.fetchLineItems(cart)));
  }

  private readonly fetchLineItems = (cart: Cart): Observable<CartWithLineItems> => {
    return this.cartService.fetchLineItems(cart)
      .pipe(
        catchError(err => {
          console.log('failed to pull line items');
          return of([]);
        }),
        map(li => this.generateCartWithLineItems(cart, li))
      );
  }

  private generateCartWithLineItems(cart: Cart, lineItems: ReadonlyArray<LineItem>): CartWithLineItems {
    return {
      cart,
      lineItems,
    };
  }

  readonly addLoadedCarts = (loaded: ReadonlyArray<Cart>) => {
    const cartWithItems = loaded.map(cart => {
      return {
        cart,
        lineItems: [],
      };
    });
    this.pulledSomeOrders = true;
    this.cartsAndItems = orderBy<CartWithLineItems>(this.cartsAndItems.concat(cartWithItems),
      [(cwi: CartWithLineItems) => cwi.cart.statusDate], ['desc']);
    this.cartsAndItemsForDisplay.next(this.cartsAndItems);
    this.ordersToView = !!this.cartsAndItems.find(cartAndItem => cartAndItem.cart.items?.length);
  }

  get isSearching(): boolean {
    return this.#isSearching;
  }

  get hasEmptySearchResults(): boolean {
    return this.#isSearching && !this.cartsWithItems().length;
  }

  cartsWithItems(): ReadonlyArray<CartWithLineItems> {
    return this.cartsAndItemsForDisplay.value.filter(cart => cart.cart.items?.length);
  }

  get totalPages(): number {
    return this.pages.length;
  }

  get currentPage(): Page<CartWithLineItems> {
    return this.pages[this.currentPageIndex] || emptyPage();
  }

  handleSearch(orderSearchCriteria: OrderSearchCriteria | undefined): void {
    this.currentPageIndex = 0;
    if (!orderSearchCriteria) {
      this.#isSearching = false;
      this.cartsAndItemsForDisplay.next(this.cartsAndItems);
    } else {
      this.#isSearching = true;
      this.cartsAndItemsForDisplay.next(this.searchHistoryService.search(
        orderSearchCriteria,
        this.cartsAndItems
      ));
    }
  }

  handlePageSelect(pageIndex: number): void {
    this.currentPageIndex = pageIndex;
    window.scrollTo({top: 0});
  }
}
